import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { HeroSlide } from "@/types/content";

type HeroCarouselProps = {
  slides: HeroSlide[];
};

const AUTOPLAY_MS = 7000;

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = slides.length;

  const goTo = useCallback(
    (index: number) => {
      setActive(((index % total) + total) % total);
    },
    [total],
  );

  useEffect(() => {
    if (paused) return undefined;
    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % total);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [paused, total, active]);

  const slide = slides[active];

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点影视推荐"
    >
      <div className="relative h-[560px] w-full md:h-[620px] lg:h-[660px]">
        {slides.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === active ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={index !== active}
          >
            <img
              src={item.image}
              alt={`${item.title}焦点剧照`}
              title={`${item.title} - 天马传媒本月焦点推荐`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/75 to-primary-950/10"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-transparent to-primary-950/50"></div>

        <div className="relative flex h-full flex-col justify-end pb-16 md:justify-center md:pb-0">
          <div className="shell w-full">
            <div key={slide.id} className="fade-up max-w-2xl pt-20">
              <h1 className="text-[11px] tracking-[0.3em] text-background-200/90 md:text-xs">
                天马传媒影视作品聚合平台
              </h1>
              <p className="mt-4 flex items-center gap-2 text-xs tracking-[0.28em] text-accent-300">
                <span className="inline-block h-px w-8 bg-accent-400"></span>
                {slide.kicker}
              </p>
              <h2 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
                {slide.title}
              </h2>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
                {slide.subtitle}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${slide.mediaId}`}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-primary-950 transition hover:bg-accent-400"
                >
                  <i className="ri-film-line text-base"></i>
                  查看作品详情
                </Link>
                <Link
                  to="/category/movie"
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/35 px-5 py-2.5 text-sm text-background-50 transition hover:border-accent-300 hover:text-accent-300"
                >
                  浏览全部作品
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-24 hidden md:block">
          <div className="shell flex items-end justify-between">
            <div className="flex items-center gap-3">
              {slides.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => goTo(index)}
                  aria-label={`切换到第${index + 1}张焦点图`}
                  className={`group flex cursor-pointer items-center gap-2 text-left ${
                    index === active ? "text-background-50" : "text-background-400 hover:text-background-200"
                  }`}
                >
                  <span className="text-[11px] tabular-nums">{String(index + 1).padStart(2, "0")}</span>
                  <span
                    className={`h-px transition-all duration-500 ${
                      index === active ? "w-10 bg-accent-400" : "w-5 bg-background-500/60"
                    }`}
                  ></span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => goTo(active - 1)}
                aria-label="上一张"
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-50 transition hover:border-accent-300 hover:text-accent-300"
              >
                <i className="ri-arrow-left-line"></i>
              </button>
              <button
                type="button"
                onClick={() => goTo(active + 1)}
                aria-label="下一张"
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-50 transition hover:border-accent-300 hover:text-accent-300"
              >
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}